/**
 * Distributors partners lookup: map, dropdown, and side panel.
 */

const DEFAULT_EMPTY_COPY = 'No partners currently listed for this state.';

function iconPhone() {
	return `<svg class="partners-lookup__icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M3.2 1.8h2.2l1 2.4-1.3 1.3a10.4 10.4 0 0 0 5.4 5.4l1.3-1.3 2.4 1v2.2c0 .6-.5 1.1-1.1 1.1C6.8 14 2 9.2 2 2.9c0-.6.5-1.1 1.2-1.1Z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>`;
}

function iconEmail() {
	return `<svg class="partners-lookup__icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><rect x="2" y="3.5" width="12" height="9" rx="1.2" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M3 4.5 8 8.4 13 4.5" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
}

function iconExternal() {
	return `<svg class="partners-lookup__icon partners-lookup__icon--external" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M6.15 4.6H3.55A1.55 1.55 0 0 0 2 6.15v6.3A1.55 1.55 0 0 0 3.55 14h6.3A1.55 1.55 0 0 0 11.4 12.45V9.85" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M7.15 8.85 14.1 1.9M10.15 1.9H14.1V5.85" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
}

function iconPerson() {
	return `<svg class="partners-lookup__icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><circle cx="8" cy="5.2" r="2.35" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M3.3 13.2c.5-2.7 2.2-4.1 4.7-4.1s4.2 1.4 4.7 4.1" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>`;
}

function escapeHtml(value) {
	return String(value ?? '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

function telHref(phone) {
	const digits = String(phone).replace(/[^\d+]/g, '');
	return digits ? `tel:${digits}` : '';
}

function locationLine(partner) {
	const city = (partner.city || '').trim();
	const state = (partner.state || '').trim();

	if (city && state) {
		return `${city}, ${state}`;
	}

	return city || state;
}

function personName(partner) {
	return [partner.first_name, partner.last_name]
		.map((part) => String(part || '').trim())
		.filter(Boolean)
		.join(' ');
}

function contactRows(partner) {
	const rows = [];
	const name = personName(partner);

	if (name) {
		rows.push(`<p class="partners-lookup__contact">${iconPerson()}<span>${escapeHtml(name)}</span></p>`);
	}

	if (partner.phone) {
		const href = telHref(partner.phone);
		rows.push(`<p class="partners-lookup__contact">${iconPhone()}<a href="${escapeHtml(href)}">${escapeHtml(partner.phone)}</a></p>`);
	}

	if (partner.email) {
		rows.push(`<p class="partners-lookup__contact">${iconEmail()}<a href="mailto:${escapeHtml(partner.email)}">${escapeHtml(partner.email)}</a></p>`);
	}

	if (partner.website) {
		const label = partner.website_label || partner.website;
		rows.push(`<p class="partners-lookup__contact">${iconExternal()}<a href="${escapeHtml(partner.website)}" target="_blank" rel="noopener noreferrer">${escapeHtml(label)}</a></p>`);
	}

	return rows.join('');
}

function renderPartner(partner, role, stateName) {
	const isSales = role === 'sales';
	const meta = isSales ? (stateName ? `${stateName} Territory` : '') : locationLine(partner);

	return `
		<article class="partners-lookup__person">
			<h4 class="partners-lookup__company">${escapeHtml(partner.company)}</h4>
			${meta ? `<p class="partners-lookup__meta">${escapeHtml(meta)}</p>` : ''}
			${contactRows(partner)}
		</article>
	`;
}

function renderSection(title, partners, role, stateName) {
	if (!partners.length) {
		return '';
	}

	return `
		<section class="partners-lookup__group">
			<h3 class="partners-lookup__group-title">${escapeHtml(title)}</h3>
			${partners.map((partner) => renderPartner(partner, role, stateName)).join('')}
		</section>
	`;
}

function parseData(root) {
	const node = root.querySelector('.js-partners-lookup-data');

	if (!node) {
		return { states: {}, partners: {}, empty_copy: DEFAULT_EMPTY_COPY };
	}

	try {
		return JSON.parse(node.textContent);
	} catch (error) {
		return { states: {}, partners: {}, empty_copy: DEFAULT_EMPTY_COPY };
	}
}

function queryState() {
	const params = new URLSearchParams(window.location.search);
	return (params.get('state') || '').toUpperCase();
}

function setQueryState(code) {
	const url = new URL(window.location.href);

	if (code) {
		url.searchParams.set('state', code.toLowerCase());
	} else {
		url.searchParams.delete('state');
	}

	window.history.replaceState({}, '', url);
}

function initPartnersLookup() {
	const root = document.querySelector('.js-partners-lookup');

	if (!root) {
		return;
	}

	const data = parseData(root);
	const emptyCopy = (data.empty_copy && String(data.empty_copy).trim()) || DEFAULT_EMPTY_COPY;
	const select = root.querySelector('.js-partners-lookup-select');
	const panel = root.querySelector('.js-partners-lookup-panel');
	const heading = root.querySelector('.js-partners-lookup-heading');
	const body = root.querySelector('.js-partners-lookup-body');
	const stateEls = root.querySelectorAll('.partners-lookup__state[data-state]');
	const map = root.querySelector('.partners-lookup__map');
	const tooltip = root.querySelector('.js-partners-lookup-tooltip');

	function highlight(code) {
		stateEls.forEach((el) => {
			el.classList.toggle('is-selected', el.getAttribute('data-state') === code);
		});
	}

	function hideTooltip() {
		if (!tooltip) {
			return;
		}

		tooltip.hidden = true;
	}

	function moveTooltip(event) {
		if (!tooltip || !map || tooltip.hidden) {
			return;
		}

		const rect = map.getBoundingClientRect();
		tooltip.style.left = `${event.clientX - rect.left}px`;
		tooltip.style.top = `${event.clientY - rect.top}px`;
	}

	function showTooltip(el, event) {
		if (!tooltip) {
			return;
		}

		tooltip.textContent = el.getAttribute('aria-label') || '';
		tooltip.hidden = false;
		moveTooltip(event);
	}

	function selectState(code, { updateUrl = true } = {}) {
		const next = (code || '').toUpperCase();
		const valid = next && data.states && data.states[next];

		if (!valid) {
			highlight('');
			if (select) {
				select.value = '';
			}
			panel.hidden = true;
			heading.textContent = '';
			body.innerHTML = '';
			if (updateUrl) {
				setQueryState('');
			}
			return;
		}

		if (select && select.value !== next) {
			select.value = next;
		}

		highlight(next);

		const entry = (data.partners && data.partners[next]) || { sales: [], distributors: [] };
		const sales = entry.sales || [];
		const distributors = entry.distributors || [];
		const salesTitle = sales.length === 1 ? 'Sales Representative' : 'Sales Representatives';
		const distTitle = distributors.length === 1 ? 'Authorized Distributor' : 'Authorized Distributors';

		const stateName = data.states[next];
		heading.textContent = stateName;
		panel.hidden = false;

		if (!sales.length && !distributors.length) {
			const empty = document.createElement('div');
			empty.className = 'partners-lookup__empty';
			empty.innerHTML = emptyCopy;
			body.replaceChildren(empty);
		} else {
			body.innerHTML = [
				renderSection(salesTitle, sales, 'sales', stateName),
				renderSection(distTitle, distributors, 'distributors', stateName),
			].join('');
		}

		if (updateUrl) {
			setQueryState(next);
		}
	}

	stateEls.forEach((el) => {
		el.addEventListener('click', () => {
			selectState(el.getAttribute('data-state'));
		});

		el.addEventListener('keydown', (event) => {
			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault();
				selectState(el.getAttribute('data-state'));
			}
		});

		el.addEventListener('mouseenter', (event) => {
			showTooltip(el, event);
		});

		el.addEventListener('mousemove', moveTooltip);

		el.addEventListener('mouseleave', hideTooltip);

		el.addEventListener('focus', () => {
			if (!tooltip) {
				return;
			}

			tooltip.textContent = el.getAttribute('aria-label') || '';
			tooltip.hidden = false;
			const rect = el.getBoundingClientRect();
			const mapRect = map.getBoundingClientRect();
			tooltip.style.left = `${rect.left + rect.width / 2 - mapRect.left}px`;
			tooltip.style.top = `${rect.top - mapRect.top}px`;
		});

		el.addEventListener('blur', hideTooltip);
	});

	if (select) {
		select.addEventListener('change', () => {
			selectState(select.value);
		});
	}

	const initial = queryState();
	if (initial && data.states[initial]) {
		selectState(initial, { updateUrl: false });
	}
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initPartnersLookup);
} else {
	initPartnersLookup();
}
